import React from 'react';

import { EditingMode } from '@aline/common';
import { Typography } from '@mui/material';
import { equals } from 'ramda';
import { AlineTooltip, ToolbarButton } from 'src/components';
import { ToggleSwitch } from 'src/components/generic';
import { NextChangeIcon } from 'src/components/icons/NextChangeIcon';
import { PreviousChangeIcon } from 'src/components/icons/PreviousChangeIcon';
import {
  useEditorTabStateActions,
  useEditorTabStateSelector,
} from 'src/editor/editor-tab-state-registry';
import { AcceptRejectMenu } from 'src/editor/toolbars/tabs/review-tab/AcceptRejectMenu';
import { ClientTransforms } from 'src/editor/transforms';
import { mostPermissiveEditingMode } from 'src/helpers';
import { useEditor, useEditorLoading } from 'src/layout-engine/hooks';
import { useEditingMode } from 'src/store/document/selectors';
import { makeStyles } from 'tss-react/mui';

import { ButtonsBarWithOverflowMenu } from '../shared/ButtonsBarWithOverflowMenu';

const useStyles = makeStyles()(theme => ({
  button: {
    marginLeft: theme.spacing(0.5),
  },
  trackChanges: {
    marginLeft: theme.spacing(1),
  },
}));

export function ReviewButtons(): JSX.Element {
  const { classes } = useStyles();
  const editor = useEditor();
  const {
    documentUi: { setEditingMode },
  } = useEditorTabStateActions();
  const editorLoading = useEditorLoading();
  const { editingMode } = useEditingMode();

  const permissionRole = useEditorTabStateSelector(state => state.documentUi.permissionRole);
  const rWHeightPx = useEditorTabStateSelector(state => state.reactWindow.windowHeight);
  const rWOffsetPx = useEditorTabStateSelector(state => state.reactWindow.scrollOffset);
  const suggestedEdits = useEditorTabStateSelector(state => state.derived.suggestedEdits, equals);
  const trackChangesActive = editingMode === EditingMode.SUGGESTING;

  function handleToggleClick(): void {
    if (!permissionRole) return;

    setEditingMode(
      trackChangesActive ? mostPermissiveEditingMode(permissionRole) : EditingMode.SUGGESTING
    );
  }

  function handleNextChange(): void {
    ClientTransforms.scrollToNextChange(editor, {
      editEntries: suggestedEdits,
      rWHeightPx,
      rWOffsetPx,
    });

    ClientTransforms.focus(editor);
  }

  function handlePrevChange(): void {
    ClientTransforms.scrollToPrevChange(editor, {
      editEntries: suggestedEdits,
      rWHeightPx,
      rWOffsetPx,
    });
    ClientTransforms.focus(editor);
  }

  return (
    <ButtonsBarWithOverflowMenu>
      <ToggleSwitch
        disabled={editorLoading}
        toggle={handleToggleClick}
        toggled={trackChangesActive}
      />
      <Typography className={classes.trackChanges} variant="caption">
        Track Changes
      </Typography>
      <AcceptRejectMenu action="accept" disabled={editorLoading} />
      <AcceptRejectMenu action="reject" disabled={editorLoading} />
      <AlineTooltip arrow title="Previous change">
        <div>
          <ToolbarButton
            className={classes.button}
            disabled={editorLoading}
            onClick={handlePrevChange}
          >
            <PreviousChangeIcon />
          </ToolbarButton>
        </div>
      </AlineTooltip>
      <AlineTooltip arrow title="Next change">
        <div>
          <ToolbarButton
            className={classes.button}
            disabled={editorLoading}
            onClick={handleNextChange}
          >
            <NextChangeIcon />
          </ToolbarButton>
        </div>
      </AlineTooltip>
    </ButtonsBarWithOverflowMenu>
  );
}
